import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import BulkUploadButton from "@/components/BulkUploadButton";

const empty = { category: "Skilled", salary_per_month: 20000, hrs_per_month: 200 };
const presets = ["Unskilled", "Semi-Skilled", "Skilled", "Highly Skilled"];

export default function Manpower() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [customCat, setCustomCat] = useState(false);

    const load = async () => { setList((await api.get("/manpower")).data); };
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setCustomCat(false); setOpen(true); };
    const startEdit = (m) => {
        setEditing(m);
        setForm({ category: m.category, salary_per_month: m.salary_per_month, hrs_per_month: m.hrs_per_month });
        setCustomCat(!presets.includes(m.category));
        setOpen(true);
    };

    const save = async () => {
        if (!form.category.trim()) { toast.error("Category required"); return; }
        const payload = {
            category: form.category,
            salary_per_month: Number(form.salary_per_month) || 0,
            hrs_per_month: Number(form.hrs_per_month) || 1,
        };
        try {
            if (editing) await api.put(`/manpower/${editing.manpower_id}`, payload);
            else await api.post("/manpower", payload);
            setOpen(false); await load(); toast.success("Saved");
        } catch { toast.error("Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete manpower entry?")) return;
        await api.delete(`/manpower/${id}`); await load();
    };

    return (
        <div>
            <PageHeader
                eyebrow="Master" title="Manpower"
                subtitle="Worker categories with monthly salary. The hourly rate is auto-derived from monthly hours."
                actions={
                    <div className="flex gap-2">
                        <BulkUploadButton entity="manpower" onComplete={load} />
                        <Button onClick={startCreate} className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" data-testid="new-manpower-button"><Plus size={16} className="mr-2" /> Add Category</Button>
                    </div>
                }
            />
            <div className="px-8 py-6">
                {list.length === 0 ? (
                    <Empty onCreate={startCreate} />
                ) : (
                    <table className="w-full sheet-table" data-testid="manpower-table">
                        <thead>
                            <tr><th>Category</th><th>Salary / Month (₹)</th><th>Hrs / Month</th><th>Rate (₹/hr)</th><th></th></tr>
                        </thead>
                        <tbody>
                            {list.map((m) => (
                                <tr key={m.manpower_id} data-testid={`manpower-row-${m.manpower_id}`}>
                                    <td className="font-medium">{m.category}</td>
                                    <td className="font-mono">{m.salary_per_month}</td>
                                    <td className="font-mono">{m.hrs_per_month}</td>
                                    <td className="font-mono font-semibold text-[#002FA7]">₹ {m.rate_per_hour}</td>
                                    <td className="text-right w-32">
                                        <button onClick={() => startEdit(m)} className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider">Edit</button>
                                        <button onClick={() => remove(m.manpower_id)} className="ml-3 text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Manpower</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Field label="Category *">
                            {!customCat ? (
                                <div className="flex gap-2">
                                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                        <SelectTrigger data-testid="manpower-category-select" className="rounded-none flex-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>{presets.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <button onClick={() => setCustomCat(true)} className="text-xs uppercase tracking-wider text-[#002FA7] hover:underline">Custom</button>
                                </div>
                            ) : (
                                <Input data-testid="manpower-category-custom" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                            )}
                        </Field>
                        <Field label="Salary / Month (₹)">
                            <Input type="number" data-testid="manpower-salary-input" value={form.salary_per_month} onChange={(e) => setForm({ ...form, salary_per_month: e.target.value })} />
                        </Field>
                        <Field label="Hours / Month">
                            <Input type="number" value={form.hrs_per_month} onChange={(e) => setForm({ ...form, hrs_per_month: e.target.value })} />
                        </Field>
                        <div className="text-xs text-zinc-500 font-mono">
                            Derived rate: ₹ {(Number(form.salary_per_month || 0) / Math.max(Number(form.hrs_per_month) || 1, 1)).toFixed(2)} / hr
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="save-manpower-button">{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
function Field({ label, children }) { return <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>{children}</div>; }
function Empty({ onCreate }) {
    return (
        <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
            <div className="tag-pill mb-4 inline-flex">Empty</div>
            <h2 className="text-2xl font-bold tracking-tight">No manpower categories</h2>
            <Button onClick={onCreate} className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#00227B]"><Plus size={16} className="mr-2" /> Add Category</Button>
        </div>
    );
}
